/**
 * usePhotoUpload — per-file sign → S3 POST → poll-to-clean lifecycle for
 * product photos (EPIC-006 Phase 7, Task 7.2).
 *
 * Adapted from `features/chat/useAttachmentUpload.ts` (same sign → upload →
 * poll-status state machine) with three product-specific changes:
 *   1. `purpose: 'product-photo'` on the sign call (vs. 'chat-document').
 *   2. Images only — JPEG / PNG / WebP (the product gallery is image-only).
 *   3. On clean it captures `s3_key_clean` (the gallery key the PATCH body's
 *      `photos[].s3_key_original` references) and the resize `variants`, so the
 *      editor can append a ready photo to its `photos[]` set.
 *
 * State transitions: uploading → scanning → ready | error.
 */
import { useCallback, useRef, useState } from "react";

import { apiClient } from "@/lib/api-client";

/** Sign endpoint response (mirrors backend `SignResponse`). */
interface SignResponse {
  upload_id: string;
  s3_key: string;
  presigned_url: string;
  presigned_fields: Record<string, string>;
}

/** Status poll response (mirrors backend `StatusResponse`). */
interface StatusResponse {
  upload_id: string;
  scan_status: "pending" | "clean" | "infected" | "failed";
  s3_key_clean?: string | null;
  variants?: Record<string, string> | null;
}

interface SignBody {
  purpose: string;
  content_type: string;
  content_length: number;
}

export type PhotoUploadState = "uploading" | "scanning" | "ready" | "error";

/** A photo draft moving through the upload lifecycle. */
export interface PhotoDraft {
  localId: string;
  state: PhotoUploadState;
  /** Local object URL for the immediate preview thumbnail. */
  previewUrl: string | null;
  /** The upload id (set after the S3 POST succeeds). */
  uploadId: string | null;
  /** The clean gallery key (set on `ready`) — the PATCH `s3_key_original`. */
  s3KeyClean: string | null;
  /** Resize-variant keys (`200`/`600`/`1200`), if the scanner produced them. */
  variants: Record<string, string> | null;
  /** i18n suffix of the failure reason on `error`. */
  errorCode: string | null;
}

const POLL_TIMEOUT_MS = 30_000;
const DEFAULT_POLL_INTERVAL_MS = 1_000;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export interface UsePhotoUploadOptions {
  /** Override poll interval in ms — primarily for tests. */
  pollIntervalMs?: number;
}

export interface UsePhotoUploadResult {
  drafts: PhotoDraft[];
  /** Add a file to the pipeline; resolves once the initial state is set. */
  add: (file: File) => Promise<PhotoDraft>;
  /**
   * Remove a draft by localId; revokes its object URL UNLESS
   * `keepPreviewUrl` is set. Pass `keepPreviewUrl: true` when a `ready`
   * draft's preview is being handed off to the gallery for continued
   * display (e.g. `PhotoUploader`'s ready→gallery handoff) — the gallery,
   * not this hook, now owns that URL, and revoking it here would break the
   * thumbnail it just handed over.
   */
  remove: (localId: string, options?: { keepPreviewUrl?: boolean }) => void;
  /** Drop every draft (e.g. after a successful save). */
  clear: () => void;
}

export function usePhotoUpload(
  options: UsePhotoUploadOptions = {},
): UsePhotoUploadResult {
  const pollMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const [drafts, setDrafts] = useState<PhotoDraft[]>([]);
  const counterRef = useRef(0);

  const patch = useCallback(
    (localId: string, next: Partial<PhotoDraft>): void => {
      setDrafts((cur) =>
        cur.map((d) => (d.localId === localId ? { ...d, ...next } : d)),
      );
    },
    [],
  );

  const add = useCallback(
    async (file: File): Promise<PhotoDraft> => {
      const localId = `photo-${counterRef.current++}`;
      const previewUrl =
        typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(file)
          : null;

      const base: PhotoDraft = {
        localId,
        state: "uploading",
        previewUrl,
        uploadId: null,
        s3KeyClean: null,
        variants: null,
        errorCode: null,
      };

      // Client-side validation (no network).
      if (!ACCEPTED_TYPES.has(file.type)) {
        const errored: PhotoDraft = {
          ...base,
          state: "error",
          errorCode: "unsupported",
        };
        setDrafts((cur) => [...cur, errored]);
        return errored;
      }
      if (file.size > MAX_BYTES) {
        const errored: PhotoDraft = {
          ...base,
          state: "error",
          errorCode: "tooLarge",
        };
        setDrafts((cur) => [...cur, errored]);
        return errored;
      }

      setDrafts((cur) => [...cur, base]);

      void (async () => {
        try {
          // 1. Sign (JWT + Idempotency-Key auto-attached).
          const sign = await apiClient.post<SignResponse, SignBody>(
            "/uploads/sign",
            {
              purpose: "product-photo",
              content_type: file.type,
              content_length: file.size,
            },
          );

          // 2. POST the file to S3 with the presigned form fields.
          const form = new FormData();
          for (const [key, value] of Object.entries(sign.presigned_fields)) {
            form.append(key, value);
          }
          form.append("file", file); // file last (S3 presigned-POST rule).

          const s3Resp = await fetch(sign.presigned_url, {
            method: "POST",
            body: form,
          });
          if (!s3Resp.ok) throw new Error("s3_upload_failed");

          patch(localId, { state: "scanning", uploadId: sign.upload_id });

          // 3. Poll until the scan completes (or timeout).
          const deadline = Date.now() + POLL_TIMEOUT_MS;
          while (true) {
            const status = await apiClient.get<StatusResponse>(
              `/uploads/${sign.upload_id}/status`,
            );
            if (status.scan_status === "clean") {
              patch(localId, {
                state: "ready",
                s3KeyClean: status.s3_key_clean ?? sign.s3_key,
                variants: status.variants ?? null,
              });
              break;
            }
            if (
              status.scan_status === "infected" ||
              status.scan_status === "failed"
            ) {
              patch(localId, {
                state: "error",
                errorCode: status.scan_status === "infected" ? "infected" : "uploadFailed",
              });
              break;
            }
            if (Date.now() > deadline) {
              patch(localId, { state: "error", errorCode: "uploadFailed" });
              break;
            }
            await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
          }
        } catch {
          patch(localId, { state: "error", errorCode: "uploadFailed" });
        }
      })();

      return base;
    },
    [patch, pollMs],
  );

  const safeRevoke = (url: string | null): void => {
    if (
      url !== null &&
      typeof URL !== "undefined" &&
      typeof URL.revokeObjectURL === "function"
    ) {
      URL.revokeObjectURL(url);
    }
  };

  const remove = useCallback(
    (localId: string, options?: { keepPreviewUrl?: boolean }): void => {
      setDrafts((cur) => {
        const target = cur.find((d) => d.localId === localId);
        if (!options?.keepPreviewUrl) {
          safeRevoke(target?.previewUrl ?? null);
        }
        return cur.filter((d) => d.localId !== localId);
      });
    },
    [],
  );

  const clear = useCallback((): void => {
    setDrafts((cur) => {
      for (const d of cur) safeRevoke(d.previewUrl);
      return [];
    });
  }, []);

  return { drafts, add, remove, clear };
}
